<template>
  <v-link
    :class="[{ '--unread': unread }, 'thread d-flex px-2']"
    :to="messageThreadUrl"
    :data-thread-id="messageThreadId"
  >
    <div :class="[{ '--private': isPrivate }, 'thread-avatar']">
      <subscriber-avatars
        :subscribers="shownSubscribers"
        :show-remaining-number="false"
        :limit="3"
        :default-text="messageThread.title"
        small
      />
    </div>
    <div class="thread-content flex-column d-flex justify-content-center">
      <div class="thread-top d-flex justify-content-between">
        <h4 class="thread-title d-flex align-items-center">
          <div v-if="unread" class="thread-unread-dot" />
          {{ messageThreadTitle }}
        </h4>
        <div class="thread-top-right d-flex align-items-center">
          <div class="thread-timestamp-ago">{{ timestampAgo }}</div>
          <v-icon v-if="!notificationsEnabled" icon="notifications_off" />
        </div>
      </div>
      <div class="thread-bottom">
        <div class="thread-latest-message">
          {{ latestMessageDescription }}
        </div>
      </div>
    </div>
  </v-link>
</template>

<script>
import { mapGetters } from "vuex"
import { timeAgo } from "@/lib/date-utils"
import { isEmpty } from "@/lib/utils"
import { MESSAGE_TYPES } from "@/lib/pusher-utils"
import { pluralize } from "@/lib/utils"

import SubscriberAvatars from "@/components/SubscriberAvatars"

export default {
  components: {
    SubscriberAvatars
  },

  props: {
    messageThread: { type: Object, required: true },
    unread: { type: Boolean, default: false }
  },

  data() {
    return {
      timestampAgo: null,
      interval: null
    }
  },

  computed: {
    ...mapGetters("messages", ["getParticipant"]),
    ...mapGetters("auth", ["currentUser"]),

    notificationsEnabled() {
      return this.getParticipant(this.messageThreadId)?.notifications_enabled
    },

    isPrivate() {
      return this.messageThread.is_private
    },

    messageThreadId() {
      return this.messageThread.id
    },

    messageThreadTitle() {
      return this.messageThread.title
    },

    shownSubscribers() {
      const subscribers = this.messageThread.message_to_participants || []

      if (this.isPrivate && subscribers.length === 1) {
        return subscribers
      }

      return subscribers.concat(this.messageThread.current_participant || [])
    },

    latestMessage() {
      return this.messageThread.latest_message
    },

    lastUser() {
      return this.latestMessage && this.latestMessage.user
    },

    latestMessageDescription() {
      if (isEmpty(this.latestMessage)) return ""

      const sender = this.isPrivate ? "" : `${this.lastUser?.name}: `
      const recipient = this.currentUser.id === this.lastUser.id ? "" : " you"

      if (this.latestMessage.message_type == MESSAGE_TYPES.IMAGE) {
        const imageCount = this.latestMessage.image_urls.length

        return `Sent${recipient} ${imageCount} ${pluralize(
          "image",
          imageCount
        )}`
      } else if (this.latestMessage.message_type == MESSAGE_TYPES.VIDEO) {
        return `Sent${recipient} a video`
      } else if (this.latestMessage.message_type == MESSAGE_TYPES.ATTACHMENT) {
        return `Sent${recipient} a link`
      }

      return this.latestMessage
        ? `${sender}${this.latestMessage.message || ""}`
        : ""
    },

    messageThreadUrl() {
      return `/me/messages/${this.messageThreadId}`
    }
  },

  mounted() {
    if (this.latestMessage) {
      this.updateTimestampAgo()

      this.interval = setInterval(() => {
        this.updateTimestampAgo()
      }, 60000)
    }
  },

  beforeDestroy() {
    clearInterval(this.interval)
  },

  methods: {
    timeAgo,
    isEmpty,

    updateTimestampAgo() {
      this.timestampAgo = timeAgo(this.latestMessage.message_ts)
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";

$unread-dot--size: 10px;
$message-thread--width: calc(100% - 40px);

.thread {
  padding: 15px 0;
  border-bottom: 1px solid $color-ui--grey-15;
  transition: background-color 0.2s;
  cursor: pointer;
  user-select: none;
  color: $color-ui--black;
  position: relative;

  &.--unread {
    .thread-title,
    .thread-latest-message {
      font-weight: 700;
    }
  }

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: $color-ui--grey-10;
  }

  &-avatar {
    display: flex;
    margin-right: 12px;
    justify-content: center;
    align-items: center;
    width: 64px;
  }

  &-content {
    width: $message-thread--width;
    overflow: hidden;
  }

  &-top {
    margin-bottom: 3px;
    font-size: 13px;
    font-weight: bold;
  }

  &-title {
    margin: 0;
    font-size: $font-size--h6;
    font-weight: 500;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &-bottom {
    font-size: 13px;
    font-weight: 400;
  }

  &-timestamp-ago {
    font-size: 11px;
    font-weight: 500;
    color: $color-ui--grey-60;
    text-align: right;
    white-space: nowrap;
  }

  &-unread-dot {
    background: $color-ui--green-dot;
    width: $unread-dot--size;
    height: $unread-dot--size;
    margin-right: 6px;
    border-radius: 50%;
  }

  &-top-right {
    .material-icons-round {
      margin-left: 5px;
      color: $color-ui--grey-60;
    }
  }

  &-latest-message {
    height: 1.2em;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    color: $color-ui--grey-80;
  }
}
</style>

<style lang="scss">
@import "@/assets/styles/variables";

.thread {
  .thread-avatar {
    .subscriber-avatar {
      background-color: $color-ui--grey-50;
      color: $color-ui--white;
    }

    .subscriber-avatars {
      .subscriber-avatar {
        margin-left: -2.4rem;

        &:first-child {
          margin-left: 0;
        }
      }
    }
  }
}

.--dark-mode {
  .thread-avatar {
    background-color: $color-ui--grey-50;
    color: $color-ui--black;
  }
}
</style>
